import {
  OpenAPI as OrdersApi,
  Order,
  OrderStatus,
  OrderInvoice,
  OrderPaymentType,
} from "@/api/SolarCloudApi";
import { Button } from "./ui/Button";
import { format, parseISO } from "date-fns";
import { error } from "./Messages";
import { doFetch } from "@/util/fetch";

type OrderCardProps = {
  order: Order;
  invoice: OrderInvoice;
};

const statusMap: Record<OrderStatus, string> = {
  [OrderStatus.DEPOSIT_TAKEN]: "Deposit",
  [OrderStatus.HOLD_TAKEN]: "Hold Taken",
  [OrderStatus.FULFILLED]: "Balance",
  [OrderStatus.PAID_IN_FULL]: "Paid in Full",
  [OrderStatus.PARTIAL_FULFILLMENT]: "Partial Fulfillment",
  [OrderStatus.PARTIAL_PAYMENT]: "Partial Payment",
  [OrderStatus.PAYMENT_PENDING]: "Payment Pending",
  [OrderStatus.PAYMENT_FAILED]: "Payment Failed",
  [OrderStatus.SUBMITTED]: "Submitted",
  [OrderStatus.CANCEL_PENDING]: "Cancel Pending",
  [OrderStatus.CANCELED]: "Canceled",
  [OrderStatus.DRAFT]: "Draft Created",
  [OrderStatus.PART_ALLOCATED]: "Part Allocated",
  [OrderStatus.ALLOCATED]: "Allocated",
};

const OrderPaymentTypeMap: Record<OrderPaymentType, string> = {
  [OrderPaymentType.DEPOSIT]: "Deposit",
  [OrderPaymentType.HOLD]: "Hold",
  [OrderPaymentType.CAPTURE]: "Capture",
  [OrderPaymentType.PAYMENT]: "Balance",
  [OrderPaymentType.REFUND]: "Refund",
};

export default function OrderCard({ order, invoice }: OrderCardProps) {
  const formattedCreated = format(
    parseISO(order.created ?? ""),
    "MMM dd, yyyy"
  );

  // const formattedUpdated = format(
  //   parseISO(order.updated ?? ""),
  //   "MMM dd, yyyy"
  // );

  // const totalBalance = order.totalPrice! - order.totalPayment!;
  const totalBalance = order.totalHold ?? 0;

  const downloadPdf = async () => {
    const token =
      OrdersApi.TOKEN === undefined
        ? undefined
        : typeof OrdersApi.TOKEN === "string"
        ? OrdersApi.TOKEN
        : await OrdersApi.TOKEN({
            method: "GET",
            url: `/api/orders/myinvoices/${invoice.uuid}/download`,
          });

    const pdfResponse = await doFetch(
      `${OrdersApi.BASE}/api/orders/myinvoices/${invoice.uuid}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!pdfResponse.ok) {
      error("Invoice PDF was not found", "Download invoice");
      return;
    }

    const pdfContents = await pdfResponse.blob();

    var url = window.URL.createObjectURL(pdfContents);

    const anchor = document.createElement("a");
    anchor.download = (order.orderNumber || order.uuid) + ".pdf";
    anchor.target = "_blank";
    anchor.href = url;
    try {
      document.body.append(anchor);

      anchor.click();
    } finally {
      document.body.removeChild(anchor);
    }
  };

  return (
    <div className="px-3 sm:px-6 py-3 sm:py-6 bg-brand-neutral-800/90 rounded-xl">
      <div className="flex justify-between items-baseline">
        <h2 className="text-lg text-white/80 mb-2">
          #{order.orderNumber} - <br className="block sm:hidden" />
          <span className="text-brand-orange-500 font-semibold">
            {/* {invoice.paymentType} */}
            {OrderPaymentTypeMap[invoice.paymentType!]}
          </span>{" "}
        </h2>
        <p className="block md:hidden text-white/80">{formattedCreated}</p>
      </div>

      <div className="justify-between items-center">
        <div className="flex items-center justify-between py-1 ">
          <div className="flex flex-1 gap-x-10 items-center">
            {/* <div className="flex flex-col justify-between h-12">
              <p className="font-semibold">Amount</p>
              <p className="text-brand-orange-500">
                {Intl.NumberFormat(undefined, {
                  style: "currency",
                  currency: "AUD",
                }).format(invoice.paymentAmount!)}
              </p>
            </div>
            <div className="flex-col justify-between h-12 hidden md:flex">
              <p className="font-semibold">Date Paid</p>
              <p className="text-brand-orange-500">{formattedCreated}</p>
            </div> */}
            {invoice.paymentType === OrderPaymentType.DEPOSIT && (
              <>
                <div className="flex flex-col justify-between h-12">
                  <p className="font-semibold">Deposit paid</p>
                  <p className="text-brand-orange-500">
                    {Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: "AUD",
                    }).format(order.totalPayment!)}
                  </p>
                </div>
                {totalBalance > 0 && (
                  <div className="flex flex-col justify-between h-12">
                    <p className="font-semibold">Balance due</p>
                    <p className="text-brand-orange-500">
                      {Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: "AUD",
                      }).format(totalBalance!)}
                    </p>
                  </div>
                )}
                <div className="flex-col justify-between h-12 hidden md:flex">
                  <p className="font-semibold">Date Paid</p>
                  <p className="text-brand-orange-500">{formattedCreated}</p>
                </div>
              </>
            )}
            {invoice.paymentType === OrderPaymentType.PAYMENT && (
              <>
                <div className="flex flex-col justify-between h-12">
                  <p className="font-semibold">Balance paid</p>
                  <p className="text-brand-orange-500">
                    {Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: "AUD",
                    }).format(totalBalance!)}
                  </p>
                </div>
                <div className="flex-col justify-between h-12 hidden md:flex">
                  <p className="font-semibold">Date Paid</p>
                  <p className="text-brand-orange-500">{formattedCreated}</p>
                </div>
              </>
            )}
          </div>
          {order.invoices && order.invoices.length > 0 && (
            <Button
              variant="ghost"
              className="py-6 border hover:text-brand-neutral-900 hover:bg-brand-blue-300 hidden sm:flex"
              onClick={downloadPdf}
            >
              Download Invoice
            </Button>
          )}
        </div>
      </div>

      {/* <div className="h-[1px] bg-brand-neutral-600/80 my-2" /> */}
      <div className="bg-brand-neutral-700/50 p-4 rounded-lg mt-4 text-white/90">
        <div className="flex font-medium border-b pb-1 mb-1 gap-0.5">
          <p className="flex-1">Name</p>
          {/* <p className="text-right">Price</p> */}
          <p className="text-center min-w-16">Panels</p>
          <p className="text-center min-w-20">Total Order</p>
        </div>
        {order.items?.map((item) => (
          <div
            key={item.uuid}
            className="py-1 border-b last:border-b-0 gap-0.5 flex "
          >
            <p className="flex-1">{item.product?.name}</p>
            {/* <p className="text-right">
              {Intl.NumberFormat(undefined, {
                style: "currency",
                currency: "AUD",
              }).format(item.unitPrice! * 100)}
            </p> */}
            <p className="text-center min-w-16">{item.units! / 100}</p>
            <p className="text-center min-w-20">
              {Intl.NumberFormat(undefined, {
                style: "currency",
                currency: "AUD",
              }).format(item.itemTotal!)}
            </p>
          </div>
        ))}
      </div>
      {order.invoices && order.invoices.length > 0 && (
        <div className="flex sm:hidden justify-end mt-4">
          <Button
            variant="ghost"
            className="py-6 border hover:text-brand-neutral-900 hover:bg-brand-blue-300"
            onClick={downloadPdf}
          >
            Download Invoice
          </Button>
        </div>
      )}
    </div>
  );
}
